import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, RotateCcw, Trash2, Inbox, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";

const BRAND_LABEL = { artwork360: "Artwork 360", medlyn360: "Medlyn 360", malansh: "Malansh (general)", other: "Other" };
export const BRAND_COLOR = { artwork360: "#5271FF", medlyn360: "#4FC3E8", malansh: "#2A3192", other: "#94A3B8" };
const FILTERS = ["all", "new", "handled"];

export const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export const Empty = ({ testId, text }) => (
  <div data-testid={testId} className="grid place-items-center rounded-3xl border border-dashed border-brand-indigo/20 py-20 text-center">
    <Inbox className="h-8 w-8 text-brand-indigo/40" />
    <p className="mt-4 text-sm text-slate-500">{text}</p>
  </div>
);

export const FilterBar = ({ filters, value, onChange, counts, testPrefix }) => (
  <div data-testid={`${testPrefix}-filters`} className="flex flex-wrap gap-2 rounded-full border border-brand-indigo/10 bg-white p-1">
    {filters.map((f) => (
      <button key={f} data-testid={`${testPrefix}-filter-${f}`} onClick={() => onChange(f)} className={`rounded-full px-4 py-2 text-xs font-semibold capitalize transition ${value === f ? "bg-brand-indigo text-white" : "text-brand-ink/60 hover:text-brand-ink"}`}>
        {f} <span className="ml-1 tabular-nums opacity-70">{counts[f] ?? 0}</span>
      </button>
    ))}
  </div>
);

const Row = ({ e, onStatus, onDelete }) => (
  <article data-testid={`enquiry-row-${e.id}`} className={`rounded-2xl border p-5 transition-colors sm:p-6 ${e.status === "handled" ? "border-brand-indigo/10 bg-white/60" : "border-brand-indigo/15 bg-white"}`}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-display text-lg font-semibold text-brand-ink">{e.name}</h3>
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-indigo/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-ink/70">
            <span className="h-2 w-2 rounded-full" style={{ background: BRAND_COLOR[e.brand] }} />{BRAND_LABEL[e.brand]}
          </span>
          <span data-testid={`enquiry-status-${e.id}`} className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${e.status === "handled" ? "bg-emerald-50 text-emerald-700" : "bg-brand-cyan/15 text-brand-indigo"}`}>{e.status}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
          <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 hover:text-brand-indigo"><Mail className="h-3.5 w-3.5" />{e.email}</a>
          {e.company && <span>{e.company}</span>}
          <span>{fmtDate(e.created_at)}</span>
        </div>
      </div>
      <div className="flex gap-2">
        {e.status === "new" ? (
          <button data-testid={`enquiry-handle-${e.id}`} onClick={() => onStatus(e, "handled")} className="inline-flex items-center gap-2 rounded-full bg-brand-indigo px-4 py-2 text-xs font-semibold text-white transition hover:bg-brand-corn"><Check className="h-3.5 w-3.5" />Mark handled</button>
        ) : (
          <button data-testid={`enquiry-reopen-${e.id}`} onClick={() => onStatus(e, "new")} className="inline-flex items-center gap-2 rounded-full border border-brand-indigo/20 px-4 py-2 text-xs font-semibold text-brand-indigo transition hover:bg-brand-ice"><RotateCcw className="h-3.5 w-3.5" />Reopen</button>
        )}
        <button data-testid={`enquiry-delete-${e.id}`} onClick={() => onDelete(e)} className="grid h-9 w-9 place-items-center rounded-full border border-red-200 text-red-500 transition hover:bg-red-50" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
    <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{e.message}</p>
  </article>
);

export const EnquiriesPanel = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/enquiries").then((r) => setItems(r.data)).catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, []);

  const shown = useMemo(() => (items || []).filter((e) => filter === "all" || e.status === filter), [items, filter]);
  const counts = useMemo(() => ({ all: items?.length || 0, new: items?.filter((e) => e.status === "new").length || 0, handled: items?.filter((e) => e.status === "handled").length || 0 }), [items]);

  const onStatus = async (e, status) => {
    try {
      const { data } = await api.patch(`/enquiries/${e.id}`, { status });
      setItems((list) => list.map((x) => (x.id === e.id ? data : x)));
      toast.success(status === "handled" ? "Marked as handled" : "Reopened");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const onDelete = async (e) => {
    if (!window.confirm(`Delete enquiry from ${e.name}?`)) return;
    try {
      await api.delete(`/enquiries/${e.id}`);
      setItems((list) => list.filter((x) => x.id !== e.id));
      toast.success("Enquiry deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-enquiries-panel">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <h2 className="font-display text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl">Enquiries</h2>
        <FilterBar filters={FILTERS} value={filter} onChange={setFilter} counts={counts} testPrefix="admin" />
      </div>
      <div className="mt-8 space-y-4">
        {items === null && <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-indigo/60">Loading enquiries…</div>}
        {items && shown.length === 0 && <Empty testId="admin-empty" text={`No ${filter === "all" ? "" : filter} enquiries yet.`} />}
        {shown.map((e) => <Row key={e.id} e={e} onStatus={onStatus} onDelete={onDelete} />)}
      </div>
    </div>
  );
};
